(() => {
  if (window.BVMHeaderCoreLoaded) return;
  window.BVMHeaderCoreLoaded = true;

  const focusableSelector = [
    'summary',
    'a[href]',
    'button:enabled',
    '[tabindex]:not([tabindex^="-"])',
    '[draggable]',
    'area',
    'input:not([type="hidden"]):enabled',
    'select:enabled',
    'textarea:enabled',
    'object',
    'iframe'
  ].join(',');

  const trapFocusHandlers = {};

  function getFocusableElements(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(focusableSelector));
  }

  function removeTrapFocus(elementToFocus = null) {
    if (trapFocusHandlers.focusin) {
      document.removeEventListener('focusin', trapFocusHandlers.focusin);
    }

    if (trapFocusHandlers.focusout) {
      document.removeEventListener('focusout', trapFocusHandlers.focusout);
    }

    if (trapFocusHandlers.keydown) {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    }

    trapFocusHandlers.focusin = null;
    trapFocusHandlers.focusout = null;
    trapFocusHandlers.keydown = null;

    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      try {
        elementToFocus.focus({ preventScroll: true });
      } catch (error) {
        elementToFocus.focus();
      }
    }
  }

  function trapFocus(container, elementToFocus = container) {
    if (!container) return;

    const elements = getFocusableElements(container);
    const first = elements[0] || container;
    const last = elements[elements.length - 1] || container;

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
      if (event.target !== container && event.target !== first && event.target !== last) return;
      document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = () => {
      document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = (event) => {
      if ((event.code || '').toUpperCase() !== 'TAB') return;

      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        first.focus();
      }

      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        last.focus();
      }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    if (elementToFocus && typeof elementToFocus.focus === 'function') {
      try {
        elementToFocus.focus({ preventScroll: true });
      } catch (error) {
        elementToFocus.focus();
      }
    }
  }

  function onKeyUpEscape(event) {
    if ((event.code || '').toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');

    if (summaryElement) {
      summaryElement.setAttribute('aria-expanded', 'false');
      summaryElement.focus();
    }
  }

  function debounce(fn, wait) {
    let timeout;

    return (...args) => {
      window.clearTimeout(timeout);
      timeout = window.setTimeout(() => fn.apply(this, args), wait);
    };
  }

  function fetchConfig(type = 'json') {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/${type}`
      }
    };
  }

  function pauseAllMedia() {
    document.querySelectorAll('.js-youtube').forEach((video) => {
      try {
        video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      } catch (error) {}
    });

    document.querySelectorAll('.js-vimeo').forEach((video) => {
      try {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
      } catch (error) {}
    });

    document.querySelectorAll('video').forEach((video) => {
      try {
        video.pause();
      } catch (error) {}
    });

    document.querySelectorAll('product-model').forEach((model) => {
      if (model.modelViewerUI) {
        try {
          model.modelViewerUI.pause();
        } catch (error) {}
      }
    });
  }

  window.getFocusableElements = window.getFocusableElements || getFocusableElements;
  window.trapFocus = window.trapFocus || trapFocus;
  window.removeTrapFocus = window.removeTrapFocus || removeTrapFocus;
  window.onKeyUpEscape = window.onKeyUpEscape || onKeyUpEscape;
  window.debounce = window.debounce || debounce;
  window.fetchConfig = window.fetchConfig || fetchConfig;
  window.pauseAllMedia = window.pauseAllMedia || pauseAllMedia;

  function initDetailsAria(root = document) {
    root.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
      if (summary.dataset.bvmHeaderCoreReady === 'true') return;
      summary.dataset.bvmHeaderCoreReady = 'true';

      const details = summary.parentNode;
      const panel = summary.nextElementSibling;
      const isDrawerSummary = Boolean(summary.closest('header-drawer, menu-drawer'));

      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', details && details.hasAttribute('open') ? 'true' : 'false');

      if (panel && panel.id) {
        summary.setAttribute('aria-controls', panel.id);
      }

      if (isDrawerSummary) return;

      summary.addEventListener('click', (event) => {
        const currentDetails = event.currentTarget.closest('details');
        const isOpen = currentDetails && currentDetails.hasAttribute('open');
        event.currentTarget.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });

      if (summary.parentElement) {
        summary.parentElement.addEventListener('keyup', onKeyUpEscape);
      }
    });
  }

  class MenuDrawer extends HTMLElement {
    constructor() {
      super();

      this.mainDetailsToggle = this.querySelector('details');
      if (!this.mainDetailsToggle) return;

      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.bindEvents();
    }

    bindEvents() {
      this.querySelectorAll('summary').forEach((summary) => {
        summary.addEventListener('click', this.onSummaryClick.bind(this));
      });

      this.querySelectorAll('button:not(.localization-selector)').forEach((button) => {
        button.addEventListener('click', this.onCloseButtonClick.bind(this));
      });
    }

    onKeyUp(event) {
      if ((event.code || '').toUpperCase() !== 'ESCAPE') return;

      const openDetailsElement = event.target.closest('details[open]');
      if (!openDetailsElement) return;

      if (openDetailsElement === this.mainDetailsToggle) {
        this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
      } else {
        this.closeSubmenu(openDetailsElement);
      }
    }

    onSummaryClick(event) {
      const summaryElement = event.currentTarget;
      const detailsElement = summaryElement.parentNode;
      const parentMenuElement = detailsElement.closest('.has-submenu');
      const isOpen = detailsElement.hasAttribute('open');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      if (detailsElement === this.mainDetailsToggle) {
        event.preventDefault();

        if (isOpen) {
          this.closeMenuDrawer(event, summaryElement);
        } else {
          detailsElement.setAttribute('open', '');
          this.openMenuDrawer(summaryElement);
        }

        if (window.matchMedia('(max-width: 990px)').matches) {
          document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        }

        return;
      }

      event.preventDefault();

      if (isOpen) {
        this.closeSubmenu(detailsElement);
        return;
      }

      detailsElement.setAttribute('open', '');
      summaryElement.setAttribute('aria-expanded', 'true');

      window.setTimeout(() => {
        detailsElement.classList.add('menu-opening');

        if (parentMenuElement) {
          parentMenuElement.classList.add('submenu-open');
        }

        const submenu = summaryElement.nextElementSibling;
        const closeButton = detailsElement.querySelector('button:not(.localization-selector)');

        if (!submenu || !closeButton) return;

        let focusApplied = false;

        const addTrapFocus = () => {
          if (focusApplied) return;
          focusApplied = true;
          trapFocus(submenu, closeButton);
          submenu.removeEventListener('transitionend', addTrapFocus);
        };

        if (reducedMotion.matches) {
          addTrapFocus();
        } else {
          submenu.addEventListener('transitionend', addTrapFocus);
          window.setTimeout(addTrapFocus, 260);
        }
      }, 100);
    }

    openMenuDrawer(summaryElement) {
      window.setTimeout(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      });

      summaryElement.setAttribute('aria-expanded', 'true');
      trapFocus(this.mainDetailsToggle, summaryElement);
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus = false) {
      if (event === undefined) return;

      this.mainDetailsToggle.classList.remove('menu-opening');

      this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
      });

      this.mainDetailsToggle.querySelectorAll('summary').forEach((summary) => {
        summary.setAttribute('aria-expanded', 'false');
      });

      this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
        submenu.classList.remove('submenu-open');
      });

      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);

      removeTrapFocus(elementToFocus || this.mainDetailsToggle.querySelector('summary'));
      this.closeAnimation(this.mainDetailsToggle);
    }

    onCloseButtonClick(event) {
      const detailsElement = event.currentTarget.closest('details');

      if (detailsElement) {
        this.closeSubmenu(detailsElement);
      }
    }

    closeSubmenu(detailsElement) {
      if (!detailsElement) return;

      const parentMenuElement = detailsElement.closest('.submenu-open');

      if (parentMenuElement) {
        parentMenuElement.classList.remove('submenu-open');
      }

      detailsElement.classList.remove('menu-opening');

      const summary = detailsElement.querySelector('summary');

      if (summary) {
        summary.setAttribute('aria-expanded', 'false');
        removeTrapFocus(summary);
      }

      this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
      if (!detailsElement) return;

      let animationStart;

      const handleAnimation = (time) => {
        if (animationStart === undefined) {
          animationStart = time;
        }

        const elapsedTime = time - animationStart;

        if (elapsedTime < 400) {
          window.requestAnimationFrame(handleAnimation);
        } else {
          detailsElement.removeAttribute('open');

          const parentOpenDetails = detailsElement.closest('details[open]');
          if (parentOpenDetails) {
            trapFocus(parentOpenDetails, detailsElement.querySelector('summary'));
          }
        }
      };

      window.requestAnimationFrame(handleAnimation);
    }
  }

  class HeaderDrawer extends MenuDrawer {
    constructor() {
      super();
    }

    openMenuDrawer(summaryElement) {
      this.header = this.header || document.querySelector('.section-header');
      const headerWrapper = this.closest('.header-wrapper');

      if (this.borderOffset === undefined) {
        this.borderOffset = headerWrapper && headerWrapper.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
      }

      if (this.header) {
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset, 10)}px`
        );

        this.header.classList.add('menu-open');
      }

      window.setTimeout(() => {
        this.mainDetailsToggle.classList.add('menu-opening');
      });

      summaryElement.setAttribute('aria-expanded', 'true');
      window.addEventListener('resize', this.onResize);
      trapFocus(this.mainDetailsToggle, summaryElement);
      document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus) {
      const focusTarget = elementToFocus || this.mainDetailsToggle.querySelector('summary');

      super.closeMenuDrawer(event, focusTarget);

      if (this.header) {
        this.header.classList.remove('menu-open');
      }

      window.removeEventListener('resize', this.onResize);
    }

    onResize = () => {
      if (this.header) {
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset, 10)}px`
        );
      }

      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initDetailsAria());
  } else {
    initDetailsAria();
  }

  if (!customElements.get('menu-drawer')) {
    customElements.define('menu-drawer', MenuDrawer);
  }

  if (!customElements.get('header-drawer')) {
    customElements.define('header-drawer', HeaderDrawer);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initDetailsAria(event.target);
  });
})();